import { useTranslation } from "react-i18next";

interface FileAnalysis {
    fileName: string;
    mesurmentName: string;
    startTime: string;
    endTime: string;
    timeStepSize: number;
    offset: number;
    dataPoints: number;
    numericDataPoints: number;
    numericPercentage: number;
}

interface AnalysisTableProps {
    analyses: FileAnalysis[];
}

export function AnalysisTable({ analyses }: AnalysisTableProps) {
    const { t } = useTranslation('dataAdjustments');

    if (!analyses || analyses.length === 0) {
        return null;
    }

    return (
        <div className="space-y-3">
            {analyses.map((analysis, index) => (
                <div
                    key={`${analysis.fileName}-${index}`}
                    className="bg-card rounded-lg border border-border overflow-hidden"
                >
                    {/* Header: File & Series Name */}
                    <div className="bg-muted px-4 py-3 border-b border-border">
                        <div className="flex flex-wrap items-center gap-x-8 gap-y-1">
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-muted-foreground uppercase">
                                    {t('table.fileName')}:
                                </span>
                                <span className="text-sm font-semibold text-foreground">
                                    {analysis.fileName}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-muted-foreground uppercase">
                                    {t('table.seriesName')}:
                                </span>
                                <span className="text-sm font-semibold text-foreground">
                                    {analysis.mesurmentName}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Data Grid */}
                    <div className="px-4 py-3">
                        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-x-4 gap-y-3 items-stretch">
                            {/* Time Range */}
                            <div className="flex flex-col justify-between">
                                <div className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase mb-0.5">
                                    {t('table.startTime')}
                                </div>
                                <div className="text-xs sm:text-sm text-foreground">
                                    {analysis.startTime}
                                </div>
                            </div>
                            <div className="flex flex-col justify-between">
                                <div className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase mb-0.5">
                                    {t('table.endTime')}
                                </div>
                                <div className="text-xs sm:text-sm text-foreground">
                                    {analysis.endTime}
                                </div>
                            </div>

                            {/* Parameters */}
                            <div className="flex flex-col justify-between">
                                <div className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase mb-0.5">
                                    {t('table.timeStepSize')}
                                </div>
                                <div className="text-xs sm:text-sm text-foreground font-medium">
                                    {analysis.timeStepSize} min
                                </div>
                            </div>
                            <div className="flex flex-col justify-between">
                                <div className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase mb-0.5">
                                    {t('table.offset')}
                                </div>
                                <div className="text-xs sm:text-sm text-foreground font-medium">
                                    {analysis.offset} min
                                </div>
                            </div>

                            {/* Statistics */}
                            <div className="flex flex-col justify-between">
                                <div className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase mb-0.5">
                                    {t('table.dataPoints')}
                                </div>
                                <div className="text-xs sm:text-sm text-foreground">
                                    {analysis.dataPoints.toLocaleString()}
                                </div>
                            </div>
                            <div className="flex flex-col justify-between">
                                <div className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase mb-0.5">
                                    {t('table.numericDataPoints')}
                                </div>
                                <div className="text-xs sm:text-sm text-foreground">
                                    {analysis.numericDataPoints.toLocaleString()}
                                </div>
                            </div>
                            <div className="flex flex-col justify-between">
                                <div className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase mb-0.5">
                                    {t('table.numericPercentage')}
                                </div>
                                <div className="text-xs sm:text-sm text-foreground font-medium">
                                    {analysis.numericPercentage.toFixed(2)}%
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default AnalysisTable;
